/**
 * New Product Dialog
 *
 * Creates a brand-new product in ShipBob (name, SKU, dimensions, weight,
 * customs origin) and records its unit cost, so it can be ordered on a PO
 * right away.
 *
 * The SKU builder follows the existing ShipBob SKU convention:
 *   BRAND-MARKET-PRODUCT-[variant tokens...]-MANUFACTURER-SOURCE
 * e.g. DZ-US-MP-QEN-WL-CH. Segment vocabularies are derived live from the
 * existing catalog so new SKUs stay consistent with it.
 *
 * The trailing SKU segment is an internal sourcing token, NOT an ISO country:
 * in the live catalog it holds values like SX (Sinomax), ZM (ZM Lash) and TC
 * (TriCol), which are manufacturers. The customs country sent to ShipBob is a
 * separate field below and is never derived from the SKU.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

const PRICE_TYPE_OPTIONS = ["DDP", "FOB"] as const;

// A "conventional" SKU: at least BRAND-MARKET-PRODUCT-...-MFR-ORIGIN
const CONVENTIONAL_SKU = /^[A-Z]{2,3}-[A-Z]{2,3}-[A-Z0-9]+(-[A-Za-z0-9]+)+$/;

interface NewProductDialogProps {
  open: boolean;
  onClose: () => void;
  /** Every SKU already in the catalog — powers the segment vocab + uniqueness check */
  existingSkus: string[];
  /** Prefill applied each time the dialog opens (e.g. from a quote line) */
  initial?: {
    name?: string | null;
    unitPrice?: number | null;
    priceType?: string | null;
  };
  onCreate: (product: {
    name: string;
    sku: string;
    unitPrice: number;
    priceType: string;
    lengthIn?: number | null;
    widthIn?: number | null;
    heightIn?: number | null;
    weightOz?: number | null;
    countryOfOrigin?: string | null;
  }) => Promise<boolean>;
}

/** Derive per-segment vocabularies from conventional SKUs in the catalog. */
function deriveVocab(existingSkus: string[]) {
  const brands = new Set<string>();
  const markets = new Set<string>();
  const productCodes = new Set<string>();
  const manufacturerCodes = new Set<string>();
  const origins = new Set<string>();

  for (const sku of existingSkus) {
    if (!CONVENTIONAL_SKU.test(sku)) continue;
    const parts = sku.split("-");
    if (parts.length < 5) continue;
    brands.add(parts[0]);
    markets.add(parts[1]);
    productCodes.add(parts[2]);
    manufacturerCodes.add(parts[parts.length - 2]);
    origins.add(parts[parts.length - 1]);
  }

  const sorted = (s: Set<string>) => Array.from(s).sort();
  return {
    brands: sorted(brands),
    markets: sorted(markets),
    productCodes: sorted(productCodes),
    manufacturerCodes: sorted(manufacturerCodes),
    origins: sorted(origins),
  };
}

export default function NewProductDialog({
  open,
  onClose,
  existingSkus,
  initial,
  onCreate,
}: NewProductDialogProps): JSX.Element {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("DZ");
  const [market, setMarket] = useState("US");
  const [productCode, setProductCode] = useState("");
  const [variantTokens, setVariantTokens] = useState("");
  const [manufacturerCode, setManufacturerCode] = useState("");
  const [origin, setOrigin] = useState("CH");
  // ISO 3166-1 alpha-2, sent to ShipBob as customs.country_code_of_origin.
  const [customsCountry, setCustomsCountry] = useState("CN");
  const [unitPrice, setUnitPrice] = useState("");
  const [priceType, setPriceType] = useState("DDP");
  const [lengthIn, setLengthIn] = useState("");
  const [widthIn, setWidthIn] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [weightLb, setWeightLb] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Apply the prefill on each open (not mid-edit) so a quote line's name and
  // price land in the form without clobbering what the user then types.
  useEffect(() => {
    if (!open || !initial) return;
    if (initial.name) setName(initial.name);
    if (initial.unitPrice != null) setUnitPrice(String(initial.unitPrice));
    if (initial.priceType) setPriceType(initial.priceType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const vocab = useMemo(() => deriveVocab(existingSkus), [existingSkus]);
  const existingSkuSet = useMemo(
    () => new Set(existingSkus.map(sku => sku.toUpperCase())),
    [existingSkus]
  );

  const normalizeSegment = (value: string) => value.trim().toUpperCase().replace(/\s+/g, "-");

  const composedSku = [
    normalizeSegment(brand),
    normalizeSegment(market),
    normalizeSegment(productCode),
    ...normalizeSegment(variantTokens).split("-").filter(Boolean),
    normalizeSegment(manufacturerCode),
    normalizeSegment(origin),
  ].filter(Boolean).join("-");

  const skuIsDuplicate = existingSkuSet.has(composedSku);
  const skuComplete = Boolean(
    normalizeSegment(brand) && normalizeSegment(market) && normalizeSegment(productCode)
      && normalizeSegment(manufacturerCode) && normalizeSegment(origin)
  );

  const parsedPrice = parseFloat(unitPrice);
  const customsCountryIsValid = /^[A-Za-z]{2}$/.test(customsCountry.trim());
  const isFormValid = name.trim().length >= 3 && skuComplete && !skuIsDuplicate
    && !isNaN(parsedPrice) && parsedPrice >= 0 && customsCountryIsValid;

  const resetForm = () => {
    setName("");
    setProductCode("");
    setVariantTokens("");
    setManufacturerCode("");
    setUnitPrice("");
    setLengthIn("");
    setWidthIn("");
    setHeightIn("");
    setWeightLb("");
    setCustomsCountry("CN");
  };

  const parseOptionalNumber = (value: string) => {
    const parsed = parseFloat(value);
    return !isNaN(parsed) && parsed > 0 ? parsed : null;
  };

  const handleCreate = async () => {
    if (!isFormValid) return;
    setIsCreating(true);
    try {
      const weightLbParsed = parseOptionalNumber(weightLb);
      const success = await onCreate({
        name: name.trim(),
        sku: composedSku,
        unitPrice: parsedPrice,
        priceType,
        lengthIn: parseOptionalNumber(lengthIn),
        widthIn: parseOptionalNumber(widthIn),
        heightIn: parseOptionalNumber(heightIn),
        weightOz: weightLbParsed != null ? Math.round(weightLbParsed * 16 * 100) / 100 : null,
        countryOfOrigin: customsCountryIsValid ? customsCountry.trim().toUpperCase() : null,
      });
      if (success) {
        resetForm();
        onClose();
      }
    } finally {
      setIsCreating(false);
    }
  };

  const segmentAutocomplete = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    options: string[],
    width: number,
  ) => (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      value={value}
      onInputChange={(_, newValue) => setValue(newValue.toUpperCase())}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={{ width }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>New Product</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates the product in ShipBob and records its cost, so it can go on a
          PO immediately. The SKU follows the catalog convention:
          BRAND-MARKET-PRODUCT-…-MANUFACTURER-ORIGIN.
        </Typography>

        <TextField
          size="small"
          fullWidth
          label="Product Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          sx={{ mb: 3 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>SKU</Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
          {segmentAutocomplete("Brand", brand, setBrand, vocab.brands, 110)}
          {segmentAutocomplete("Market", market, setMarket, vocab.markets, 110)}
          {segmentAutocomplete("Product", productCode, setProductCode, vocab.productCodes, 140)}
          <TextField
            size="small"
            label="Variant (size, color…)"
            placeholder="e.g. QEN or ZONE-KNG"
            value={variantTokens}
            onChange={(e) => setVariantTokens(e.target.value.toUpperCase())}
            sx={{ width: 190 }}
          />
          {segmentAutocomplete("Mfr", manufacturerCode, setManufacturerCode, vocab.manufacturerCodes, 100)}
          {segmentAutocomplete("Source", origin, setOrigin, vocab.origins, 100)}
        </Box>
        <Alert severity={skuIsDuplicate ? "error" : skuComplete ? "success" : "info"} sx={{ mb: 3 }}>
          {skuIsDuplicate
            ? `${composedSku} already exists in the catalog`
            : skuComplete
              ? `SKU: ${composedSku}`
              : "Fill brand, market, product, manufacturer, and source to compose the SKU"}
        </Alert>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Cost</Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            size="small"
            type="number"
            label="Unit Cost"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            required
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ width: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Basis</InputLabel>
            <Select value={priceType} label="Basis" onChange={(e) => setPriceType(e.target.value)}>
              {PRICE_TYPE_OPTIONS.map(pt => (
                <MenuItem key={pt} value={pt}>{pt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Unit dimensions &amp; weight (optional — used by ShipBob for storage/fulfillment billing)
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField size="small" type="number" label="Length (in)" value={lengthIn}
            onChange={(e) => setLengthIn(e.target.value)} inputProps={{ min: 0, step: 0.1 }} sx={{ width: 130 }} />
          <TextField size="small" type="number" label="Width (in)" value={widthIn}
            onChange={(e) => setWidthIn(e.target.value)} inputProps={{ min: 0, step: 0.1 }} sx={{ width: 130 }} />
          <TextField size="small" type="number" label="Height (in)" value={heightIn}
            onChange={(e) => setHeightIn(e.target.value)} inputProps={{ min: 0, step: 0.1 }} sx={{ width: 130 }} />
          <TextField size="small" type="number" label="Weight (lb)" value={weightLb}
            onChange={(e) => setWeightLb(e.target.value)} inputProps={{ min: 0, step: 0.1 }} sx={{ width: 130 }} />
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Customs</Typography>
        <TextField
          size="small"
          label="Country of Origin (ISO)"
          value={customsCountry}
          onChange={(e) => setCustomsCountry(e.target.value.toUpperCase().slice(0, 2))}
          required
          error={!customsCountryIsValid}
          helperText="Two-letter ISO country where the goods are made (CN = China). Declared to customs — not the SKU's sourcing token."
          inputProps={{ maxLength: 2 }}
          sx={{ width: 260 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreating}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isFormValid || isCreating}
        >
          {isCreating ? "Creating in ShipBob…" : "Create Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
